import React, { useState } from "react";
import { Popover } from "antd5";

import { useSubscription } from "lib/hooks/subscription";
import { EventData } from "lib/tracking";
import PaywallModal from "./PaywallModal";
import { convertFeatureTypeToDataType, FeatureType, PaidTag, PaywallImg } from "./paywallUtils";

import css from "./PaywallPopover.module.scss";

type PopoverContent = {
  title: string;
  description: string;
  image: JSX.Element;
};

type PaywallPopoverContent = Partial<Record<FeatureType, PopoverContent>>;

export const popoverContent: PaywallPopoverContent = {
  AWARDS: {
    title: "Expiring contracts",
    description:
      "Use expiring contracts to identify upcoming opportunities and build relationships, before the tender is written.",
    image: <PaywallImg context={"POPOVER"} featureType={"AWARDS"} />,
  },

  AWARDS_LOCKED: {
    title: "Upgrade for full access to expiring contracts",
    description:
      "You have reached the free viewing limit of expiring contracts. For full access to continue building proactive pipeline, upgrade your plan.",
    image: <PaywallImg context={"POPOVER"} featureType={"AWARDS_LOCKED"} />,
  },

  PARTNERS: {
    title: "Partner intelligence",
    description:
      "Map your partner network to identify contracts they've won, as well as upcoming opportunities you can pursue.",
    image: <PaywallImg context={"POPOVER"} featureType={"PARTNERS"} />,
  },

  COMPETITORS: {
    title: "Competitor intelligence",
    description:
      "Map your competitors to identify contracts they've won and upcoming opportunities you can infiltrate.",
    image: <PaywallImg context={"POPOVER"} featureType={"COMPETITORS"} />,
  },

  BUYERS: {
    title: "Buyer intelligence",
    description:
      "View profiles for target accounts and existing customers, identifying contracts they’ve won and upcoming opportunities you can pursue.",
    image: <PaywallImg context={"POPOVER"} featureType={"BUYERS"} />,
  },

  SUPPLIERS: {
    title: "Supplier intelligence",
    description:
      "View profiles of potential partners and competitors and leverage custom dashboards to identify contracts they've won and upcoming opportunities you can pursue.",
    image: <PaywallImg context={"POPOVER"} featureType={"SUPPLIERS"} />,
  },

  CONTACTS: {
    title: "Decision maker contacts",
    description:
      "Go beyond procurement contacts and reach out to decision-makers directly. Unlock contact details and generate personalised outreach in seconds with Stotles AI.",
    image: <PaywallImg context={"POPOVER"} featureType={"CONTACTS"} />,
  },

  DOCUMENTS: {
    title: "Strategic documents",
    description:
      "Get access to thousands of buyer-related documents, including budgets, meeting minutes, delivery plans, strategy documents, and more.",
    image: <PaywallImg context={"POPOVER"} featureType={"DOCUMENTS"} />,
  },

  SPEND: {
    title: "Spend data",
    description:
      "Analysing invoice-level spend data can help you uncover deeper relationships, market opportunities, and industry insights.",
    image: <PaywallImg context={"POPOVER"} featureType={"SPEND"} />,
  },

  ACCOUNT_BRIEFINGS: {
    title: "Custom Briefings",
    description:
      "A suite of custom briefings designed to help commercial teams save hundreds of hours researching and spend more time closing deals.",
    image: <PaywallImg context={"POPOVER"} featureType={"ACCOUNT_BRIEFINGS"} />,
  },

  FRAMEWORKS: {
    title: "Upgrade to unlock Framework data",
    description:
      "Sift through thousands of frameworks in minutes to discover and qualify the most valuable opportunities and routes to market for your business.",
    image: <PaywallImg context={"POPOVER"} featureType={"FRAMEWORKS"} />,
  },

  EXPORTS: {
    title: "Export your search results to CSV and XLSX",
    description:
      "Download unlimited rows to perform your own custom analysis powered by Stotles data.",
    image: <PaywallImg context={"POPOVER"} featureType={"EXPORTS"} />,
  },

  BIDS: {
    title: "Upgrade to create unlimited bid / no-bid reports",
    description:
      "You've hit your limit of free bid / no-bid reports. Upgrade to let our team know you're interested in Bid Studio.",
    image: <PaywallImg context={"POPOVER"} featureType={"BIDS"} />,
  },

  CREATE_BIDS: {
    title: "Unlock Bid Studio to turn your pipeline into bid proposals in minutes.",
    description:
      "Draft bids that pull from the UK's most complete source of government intelligence.",
    image: <PaywallImg context={"POPOVER"} featureType={"BIDS"} />,
  },

  STRATEGY: {
    title: "Uncover your true TAM - and the best routes to access it.",
    description:
      "Size and sort your market—break it down by territories, accounts, and frameworks to build a sharper go-to-market strategy.",
    image: <PaywallImg context={"POPOVER"} featureType={"STRATEGY"} />,
  },

  BID_NO_BID_REPORTS: {
    title: "Upgrade to unlock more bid/no bid reports",
    description:
      "Get access to more tender document summaries that help you understand the bid requirements and guide your bid/no-bid decision",
    image: <PaywallImg context={"POPOVER"} featureType={"BID_NO_BID_REPORTS"} />,
  },

  BID_RESPONSES: {
    title: "Upgrade to unlock bid responses",
    description:
      "Use AI to extract bid questions from tender document and draft winning bids, as well as manage your bid writing team's progress",
    image: <PaywallImg context={"POPOVER"} featureType={"BID_RESPONSES"} />,
  },

  BID_STOTLES_AI: {
    title: "Upgrade to unlock Stotles AI",
    description:
      "Get access to powerful summarisation, ask questions of the tender documents and analyse requirements against your content library.",
    image: <PaywallImg context={"POPOVER"} featureType={"BID_STOTLES_AI"} />,
  },

  BID_WIN_THEMES: {
    title: "Upgrade to unlock win themes.",
    description:
      "Use Stotles AI to recommend potential win themes connecting the dots across market data, your content library and the requirements. Available as part of Bid Studio plans.",
    image: <PaywallImg context={"POPOVER"} featureType={"BID_WIN_THEMES"} />,
  },

  CMS_INTEGRATIONS: {
    title: "Upgrade to use content library and integrations",
    description:
      "Stotles content library and integration with your existing content library are available as part of paid Bid Studio plans.",
    image: <PaywallImg context={"POPOVER"} featureType={"CMS_INTEGRATIONS"} />,
  },
};

type Props = {
  children: React.ReactNode;
  featureType: FeatureType;
  showModalOnClick?: boolean;
  contextSource?: string;
  // Just to pass tests for now
  manualOpen?: boolean;
  trackingData?: EventData;
  video?: boolean;
  isDisabled?: boolean;
};

function PaywallPopover({
  children,
  featureType,
  manualOpen,
  contextSource,
  trackingData,
  showModalOnClick = true,
  video = false,
  isDisabled = false,
}: Props) {
  const [popoverOpen, setPopoverOpen] = useState<boolean>(manualOpen ?? false);
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const subscription = useSubscription();

  const blockAccess = !subscription.hasDataTypes(convertFeatureTypeToDataType(featureType));

  const content = popoverContent[featureType];

  const openPaywallModal = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (showModalOnClick && blockAccess) {
      // prevent submit/reset type buttons from refreshing page when paywall visible
      e.preventDefault();
      e.stopPropagation();

      setModalOpen(true);
    }
  };

  if (!content || isDisabled || !blockAccess) {
    return <>{children}</>;
  }

  const { title, description, image } = content;

  const modalOnOpenChange = (open: boolean) => {
    setModalOpen(open);
  };

  return (
    <>
      <Popover
        placement="bottomLeft"
        arrow={false}
        open={popoverOpen}
        defaultOpen={false}
        onOpenChange={(open) => {
          if (blockAccess) setPopoverOpen(open);
        }}
        zIndex={9998}
        mouseEnterDelay={1}
        overlayInnerStyle={{ padding: 0 }}
        content={
          <div className={css.popoverContainer}>
            {image}
            <div className={css.popoverContent}>
              <PaidTag />
              <h3>{title}</h3>
              <p data-testid="description">{description}</p>
            </div>
          </div>
        }
      >
        <div onClick={openPaywallModal}>{children}</div>
      </Popover>
      <PaywallModal
        open={modalOpen}
        onOpenChange={modalOnOpenChange}
        featureType={featureType}
        contextSource={contextSource}
        trackingData={trackingData}
        video={video}
      />
    </>
  );
}

export default PaywallPopover;
